'use client'

import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import Link from 'next/link'

export type CoachStep = {
  target?: string // CSS selector to spotlight; omit for a centered card
  title: string
  body: string
}
export type CoachConfig = {
  steps: CoachStep[]
  ctaHref: string
  ctaLabel: string
}

const PAD = 12
const CARD_W = 360
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))

// App-style coachmark tour: dims the page and spotlights a real nav element each step, with a
// callout explaining what it does. On mobile it opens the hamburger menu so the links are visible.
export function TourCoach({ config, onClose }: { config: CoachConfig; onClose: () => void }) {
  const { steps, ctaHref, ctaLabel } = config
  const [i, setI] = useState(0)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const last = steps.length - 1
  const step = steps[i]

  const measure = useCallback(() => {
    if (!step.target) {
      setRect(null)
      return
    }
    const el = document.querySelector(step.target) as HTMLElement | null
    setRect(el ? el.getBoundingClientRect() : null)
  }, [step.target])

  // Open the mobile menu (if collapsed) so nav targets exist on screen; restore on exit.
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    const isMobile = window.matchMedia('(max-width: 760px)').matches
    const toggle = document.querySelector('.nav-toggle') as HTMLElement | null
    const links = document.querySelector('.nav-links')
    let openedMenu = false
    if (isMobile && toggle && links && !links.classList.contains('open')) {
      toggle.click()
      openedMenu = true
    }
    return () => {
      document.body.style.overflow = ''
      if (openedMenu && toggle) toggle.click()
    }
  }, [])

  useLayoutEffect(() => {
    // let the menu open / layout settle, then measure
    const id = requestAnimationFrame(measure)
    return () => cancelAnimationFrame(id)
  }, [measure, i])

  useEffect(() => {
    const on = () => measure()
    window.addEventListener('resize', on)
    window.addEventListener('scroll', on, true)
    return () => {
      window.removeEventListener('resize', on)
      window.removeEventListener('scroll', on, true)
    }
  }, [measure])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight') setI((n) => Math.min(last, n + 1))
      else if (e.key === 'ArrowLeft') setI((n) => Math.max(0, n - 1))
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [last, onClose])

  const spot = rect
    ? { top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 }
    : null

  let cardStyle: React.CSSProperties = {}
  if (spot && typeof window !== 'undefined') {
    const below = spot.top < window.innerHeight * 0.5
    const left = clamp(spot.left + spot.width / 2 - CARD_W / 2, 16, window.innerWidth - CARD_W - 16)
    cardStyle = below
      ? { top: spot.top + spot.height + 18, left }
      : { top: Math.max(16, spot.top - 18), left, transform: 'translateY(-100%)' }
  }

  return (
    <div className="coach" role="dialog" aria-modal="true" aria-label="Site tour">
      {/* click-blocker (so the page underneath isn't interactive); tapping it advances */}
      <div className="coach__block" onClick={() => setI((n) => (n < last ? n + 1 : n))} />
      {spot ? (
        <div className="coach__spot" style={spot} />
      ) : (
        <div className="coach__backdrop" />
      )}
      <div className={`coach__card${spot ? '' : ' coach__card--center'}`} style={spot ? cardStyle : undefined}>
        <div className="coach__dots" aria-label={`Step ${i + 1} of ${steps.length}`}>
          {steps.map((_, n) => (
            <span key={n} className={`coach__dot${n === i ? ' on' : ''}${n < i ? ' done' : ''}`} />
          ))}
        </div>
        <h3>{step.title}</h3>
        <p>{step.body}</p>
        <div className="coach__controls">
          <button className="coach__skip" onClick={onClose}>
            Skip tour
          </button>
          <span className="coach__nav">
            {i > 0 ? (
              <button className="coach__btn" onClick={() => setI(i - 1)}>
                Back
              </button>
            ) : null}
            {i < last ? (
              <button className="coach__btn coach__btn--primary" onClick={() => setI(i + 1)}>
                Next
              </button>
            ) : (
              <Link className="coach__btn coach__btn--primary" href={ctaHref}>
                {ctaLabel}
              </Link>
            )}
          </span>
        </div>
      </div>
    </div>
  )
}
